import { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Textarea, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { useCompleteTask, useLawyers, useUpdateTask } from '../hooks'
import { STATUS_OPTIONS } from '../status'
import type { Task, TaskStatus } from '../types'

export interface EditTaskModalProps {
  task: Task | null
  onClose: () => void
  /** `false` → podgląd bez zapisu (brak `tasks.manage`). */
  canManage: boolean
}

/**
 * Modal edycji zadania: tytuł, opis, status, termin, przypisany + „Zakończ".
 * Zamiast usuwania — status „Anulowane" (backend nie ma DELETE; ślad zostaje).
 */
export function EditTaskModal({ task, onClose, canManage }: EditTaskModalProps) {
  const toast = useToast()
  const lawyers = useLawyers(Boolean(task) && canManage)
  const update = useUpdateTask()
  const complete = useCompleteTask()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<TaskStatus>('open')
  const [dueDate, setDueDate] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!task) return
    setTitle(task.title)
    setDescription(task.description ?? '')
    setStatus(task.status)
    setDueDate(task.due_date ?? '')
    setAssigneeId(task.assignee_id ? String(task.assignee_id) : '')
    setError(null)
  }, [task])

  if (!task) return null

  const save = async () => {
    setError(null)
    if (!title.trim()) {
      setError('Tytuł zadania nie może być pusty.')
      return
    }
    try {
      await update.mutateAsync({
        id: task.id,
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        status,
        due_date: dueDate || null,
        assignee_id: assigneeId ? Number(assigneeId) : null,
      })
      toast.success('Zadanie zapisane')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zapisać zadania.')
    }
  }

  const finish = async () => {
    setError(null)
    try {
      await complete.mutateAsync(task.id)
      toast.success('Zadanie zakończone')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zakończyć zadania.')
    }
  }

  const busy = update.isPending || complete.isPending

  return (
    <Modal
      open
      onClose={onClose}
      title={task.title}
      subtitle={[
        task.case ? `${task.case.signature} · ${task.case.client_name}` : null,
        task.due_date ? `termin ${formatDate(task.due_date)}` : 'bez terminu',
        task.created_by_id === null ? 'utworzone z dekretacji pisma' : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      footer={
        <>
          {canManage && (task.status === 'open' || task.status === 'in_progress') ? (
            <Button variant="secondary" onClick={finish} loading={complete.isPending} disabled={busy}>
              Zakończ
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {canManage ? 'Anuluj' : 'Zamknij'}
          </Button>
          {canManage ? (
            <Button variant="primary" onClick={save} loading={update.isPending} disabled={busy}>
              Zapisz
            </Button>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Input
            label="Tytuł zadania"
            value={title}
            disabled={!canManage}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <Select
          label="Status"
          value={status}
          disabled={!canManage}
          onChange={(event) => setStatus(event.target.value as TaskStatus)}
          options={STATUS_OPTIONS}
        />
        <Select
          label="Przypisany"
          value={assigneeId}
          disabled={!canManage}
          onChange={(event) => setAssigneeId(event.target.value)}
          options={[
            { value: '', label: 'nieprzypisane' },
            ...(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.full_name,
            })),
          ]}
        />
        <Input
          label="Termin"
          type="date"
          value={dueDate}
          disabled={!canManage}
          onChange={(event) => setDueDate(event.target.value)}
        />
        <div className="col-span-2">
          <Textarea
            label="Opis"
            rows={3}
            value={description}
            disabled={!canManage}
            placeholder="Zakres pracy, materiały wejściowe, oczekiwany rezultat…"
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </div>
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
